"use client";

import { useRef, useState } from "react";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Heading } from "@/src/components/ui/Typography";

import type { IndustryItemData } from "./IndustriesServed.types";
import { IndustryRow } from "./IndustryRow";

interface IndustriesServedProps {
  title: string;
  items: IndustryItemData[];
}

const MAX_VISIBLE_INDUSTRIES = 3;

export function IndustriesServed({ title, items }: IndustriesServedProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<"up" | "down">("down");
  const prevIndex = useRef(0);

  const visibleItems = items.slice(0, MAX_VISIBLE_INDUSTRIES);

  if (visibleItems.length === 0) return null;

  const handleHover = (index: number) => {
    if (index === activeIndex) return;
    setDirection(index > prevIndex.current ? "down" : "up");
    prevIndex.current = index;
    setActiveIndex(index);
  };

  const handleLeave = () => handleHover(0);

  return (
    <Section className="bg-neutral-100">
      <Container>
        <Heading as="h2" size="displayLG" className="max-w-150">
          {title}
        </Heading>

        <div
          onMouseLeave={handleLeave}
          className="mt-16 flex flex-col border-t border-neutral-200"
        >
          {visibleItems.map((item, index) => (
            <IndustryRow
              key={item.name}
              item={item}
              active={index === activeIndex}
              direction={direction}
              onHover={() => handleHover(index)}
            />
          ))}
        </div>
      </Container>
    </Section>
  );
}
